import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import type {
  EditorMode,
  EditorConfig,
  EditorParams,
  Instruction,
  HistoryAction,
  Toast,
  AIRecommendation,
  AIPreview,
  VariantId,
  Variant,
  DraftState,
  KeyboardShortcut,
} from '../types';
import { EDITOR_CONFIG_DEFAULTS } from '../styles/constants';
import { formatErrorForToast } from '../utils/errors';
import { instructionTextValue } from '../utils/instructionText';

// Viewport types
export interface ViewportSize {
  name: string;
  width: number;
  height: number;
}

// State interface
interface EditorState {
  // Core state
  params: EditorParams;
  config: EditorConfig;
  mode: EditorMode;
  selectedElement: Element | null;
  
  // Deployment mode (NEW)
  deploymentMode: 'direct' | 'iframe';
  parentOrigin?: string;
  
  // Test and auth info
  testId?: string;
  authToken?: string;
  
  // Variant management
  currentVariantId: VariantId;
  variants: Map<VariantId, Variant>;
  
  // Instructions for backend
  instructions: Instruction[];
  hasUnsavedChanges: boolean;
  
  // History for undo/redo
  history: HistoryAction[];
  historyIndex: number;
  
  // Hover tracking
  hoveredElement: Element | null;
  activeTooltips: Map<Element, HTMLElement>;
  
  // AI state
  aiRecommendations: AIRecommendation[];
  isAILoading: boolean;
  aiPreview: AIPreview | null;
  
  // Draft mode
  draftState: DraftState;
  
  // Toast notifications
  toasts: Toast[];
  
  // Loading states
  isSaving: boolean;
  isLoadingVariant: boolean;
  
  // Viewport state (for iframe mode communication)
  viewport: ViewportSize;
  
  // Original DOM HTML (for variant switching reset)
  originalBodyHTML: string | null;
}

// Action types
type EditorAction =
  | { type: 'SET_MODE'; mode: EditorMode; element?: Element | null }
  | { type: 'SET_SELECTED_ELEMENT'; element: Element | null }
  | { type: 'SET_HOVERED_ELEMENT'; element: Element | null }
  | { type: 'ADD_INSTRUCTION'; instruction: Instruction }
  | { type: 'CLEAR_INSTRUCTIONS' }
  | { type: 'ADD_HISTORY_ACTION'; action: HistoryAction }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'CLEAR_HISTORY' }
  | { type: 'SET_AI_RECOMMENDATIONS'; recommendations: AIRecommendation[] }
  | { type: 'SET_AI_LOADING'; isLoading: boolean }
  | { type: 'SET_AI_PREVIEW'; preview: AIPreview | null }
  | { type: 'ADD_TOAST'; toast: Toast }
  | { type: 'REMOVE_TOAST'; id: string }
  | { type: 'ADD_TOOLTIP'; element: Element; tooltip: HTMLElement }
  | { type: 'REMOVE_TOOLTIP'; element: Element }
  | { type: 'CLEAR_TOOLTIPS' }
  | { type: 'SWITCH_VARIANT'; variantId: VariantId }
  | { type: 'CREATE_VARIANT'; variant: Variant }
  | { type: 'UPDATE_VARIANT'; variantId: VariantId; updates: Partial<Variant> }
  | { type: 'SET_DRAFT_MODE'; enabled: boolean }
  | { type: 'TOGGLE_PREVIEW_MODE' }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_LOADING_VARIANT'; isLoading: boolean }
  | { type: 'SET_VIEWPORT'; viewport: ViewportSize }
  | { type: 'SET_ORIGINAL_HTML'; html: string }
  | { type: 'REMOVE_LAST_INSTRUCTION' }
  | { type: 'SET_INSTRUCTIONS'; instructions: Instruction[] };

// Initial state
function createInitialState(params: EditorParams): EditorState {
  const currentVariantId: VariantId = (params.variantId as VariantId) || 'B';
  
  // Initialize variants map with default variants
  const variants = new Map<VariantId, Variant>();
  const variantIds: VariantId[] = ['A', 'B'];
  
  variantIds.forEach((id) => {
    variants.set(id, {
      id,
      name: id === 'A' ? 'Original (A)' : `Variant ${id}`,
      instructions: [],
      createdAt: new Date(),
      modifiedAt: new Date(),
      isControl: id === 'A',
    });
  });
  
  return {
    params,
    config: EDITOR_CONFIG_DEFAULTS,
    mode: null,
    selectedElement: null,
    deploymentMode: params.deploymentMode || 'direct',
    parentOrigin: params.parentOrigin,
    currentVariantId,
    variants,
    instructions: [],
    hasUnsavedChanges: false,
    history: [],
    historyIndex: -1,
    hoveredElement: null,
    activeTooltips: new Map(),
    aiRecommendations: [],
    isAILoading: false,
    aiPreview: null,
    draftState: {
      enabled: false,
      originalInstructions: [],
      previewMode: false,
    },
    toasts: [],
    isSaving: false,
    isLoadingVariant: false,
    viewport: { name: 'Desktop', width: 1920, height: 1080 },
    originalBodyHTML: null,
  };
}

// Reducer
function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SET_MODE':
      return {
        ...state,
        mode: action.mode,
        selectedElement: action.element !== undefined ? action.element : state.selectedElement,
      };

    case 'SET_SELECTED_ELEMENT':
      return { ...state, selectedElement: action.element };

    case 'SET_HOVERED_ELEMENT':
      return { ...state, hoveredElement: action.element };

    case 'ADD_INSTRUCTION':
      return {
        ...state,
        instructions: [...state.instructions, action.instruction],
        hasUnsavedChanges: true,
      };

    case 'CLEAR_INSTRUCTIONS':
      return {
        ...state,
        instructions: [],
        hasUnsavedChanges: false,
      };

    case 'ADD_HISTORY_ACTION':
    {
      // Remove any actions after current index (branch pruning)
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      newHistory.push(action.action);
      return {
        ...state,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      };
    }

    case 'UNDO':
      if (state.historyIndex >= 0) {
        const newIndex = state.historyIndex - 1;
        return {
          ...state,
          historyIndex: newIndex,
        };
      }
      console.warn('⚠️ [EditorContext/Reducer] UNDO - cannot go back, already at beginning');
      return state;

    case 'REDO':
      if (state.historyIndex < state.history.length - 1) {
        const newIndex = state.historyIndex + 1;
        return {
          ...state,
          historyIndex: newIndex,
        };
      }
      console.warn('⚠️ [EditorContext/Reducer] REDO - cannot go forward, at end of history');
      return state;

    case 'CLEAR_HISTORY':
      return {
        ...state,
        history: [],
        historyIndex: -1,
      };

    case 'SET_AI_RECOMMENDATIONS':
      return {
        ...state,
        aiRecommendations: action.recommendations,
      };

    case 'SET_AI_LOADING':
      return {
        ...state,
        isAILoading: action.isLoading,
      };
      
    case 'SET_AI_PREVIEW':
      return {
        ...state,
        aiPreview: action.preview,
      };

    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.toast],
      };

    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };

    case 'ADD_TOOLTIP':
    {
      const newTooltips = new Map(state.activeTooltips);
      newTooltips.set(action.element, action.tooltip);
      return {
        ...state,
        activeTooltips: newTooltips,
      };
    }

    case 'REMOVE_TOOLTIP':
    {
      const tooltips = new Map(state.activeTooltips);
      tooltips.delete(action.element);
      return {
        ...state,
        activeTooltips: tooltips,
      };
    }

    case 'CLEAR_TOOLTIPS':
      return {
        ...state,
        activeTooltips: new Map(),
      };
      
    case 'SWITCH_VARIANT':
    {
      const targetVariant = state.variants.get(action.variantId);
      if (!targetVariant) return state;
      
      return {
        ...state,
        currentVariantId: action.variantId,
        instructions: [...targetVariant.instructions],
        hasUnsavedChanges: false,
        history: [],
        historyIndex: -1,
      };
    }
      
    case 'CREATE_VARIANT':
    {
      const newVariants = new Map(state.variants);
      newVariants.set(action.variant.id, action.variant);
      return {
        ...state,
        variants: newVariants,
      };
    }
      
    case 'UPDATE_VARIANT':
    {
      const updatedVariants = new Map(state.variants);
      const variantToUpdate = updatedVariants.get(action.variantId);
      if (variantToUpdate) {
        updatedVariants.set(action.variantId, {
          ...variantToUpdate,
          ...action.updates,
          modifiedAt: new Date(),
        });
      }
      return {
        ...state,
        variants: updatedVariants,
      };
    }
      
    case 'SET_DRAFT_MODE':
      return {
        ...state,
        draftState: {
          ...state.draftState,
          enabled: action.enabled,
          originalInstructions: action.enabled ? [...state.instructions] : [],
        },
      };
      
    case 'TOGGLE_PREVIEW_MODE':
      return {
        ...state,
        draftState: {
          ...state.draftState,
          previewMode: !state.draftState.previewMode,
        },
      };
      
    case 'SET_SAVING':
      return {
        ...state,
        isSaving: action.isSaving,
      };
      
    case 'SET_LOADING_VARIANT':
      return {
        ...state,
        isLoadingVariant: action.isLoading,
      };

    case 'SET_VIEWPORT':
      return {
        ...state,
        viewport: action.viewport,
      };

    case 'SET_ORIGINAL_HTML':
      return {
        ...state,
        originalBodyHTML: action.html,
      };

    case 'REMOVE_LAST_INSTRUCTION':
      return {
        ...state,
        instructions: state.instructions.slice(0, -1),
        hasUnsavedChanges: state.instructions.length > 1,
      };

    case 'SET_INSTRUCTIONS': {
      // Saved baseline: not dirty, no history. Undo only covers edits made this session.
      const updatedVariants = new Map(state.variants);
      const current = updatedVariants.get(state.currentVariantId);
      if (current) {
        updatedVariants.set(state.currentVariantId, {
          ...current,
          instructions: action.instructions,
          modifiedAt: new Date(),
        });
      }
      return {
        ...state,
        variants: updatedVariants,
        instructions: action.instructions,
        hasUnsavedChanges: false,
        history: [],
        historyIndex: -1,
      };
    }

    default:
      return state;
  }
}

// Context
interface EditorContextValue {
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  
  // Convenience methods
  setMode: (mode: EditorMode, element?: Element | null) => void;
  closeEditor: () => void;
  addInstruction: (instruction: Instruction) => void;
  addHistoryAction: (action: HistoryAction) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  showToast: (message: string, variant?: Toast['variant'], description?: string, duration?: number) => void;
  removeToast: (id: string) => void;
  
  // Variant management
  switchVariant: (variantId: VariantId) => void;
  createVariant: (id: VariantId, fromCurrent?: boolean) => void;
  getCurrentVariant: () => Variant | undefined;
  getVariantList: () => Variant[];
  
  // AI features
  setAIPreview: (preview: AIPreview | null) => void;
  applyAIPreview: () => void;
  cancelAIPreview: () => void;
  
  // Draft mode
  enableDraftMode: () => void;
  disableDraftMode: () => void;
  togglePreviewMode: () => void;
  
  // Save with better handling
  saveChanges: () => Promise<void>;

  // Replay a saved draft into the DOM without marking it dirty
  hydrateInstructions: (instructions: Instruction[]) => void;
  
  // Viewport control
  setViewport: (viewport: ViewportSize) => void;
}

const EditorContext = createContext<EditorContextValue | null>(null);

// Provider
interface EditorProviderProps {
  params: EditorParams;
  children: ReactNode;
}

export function EditorProvider({ params, children }: EditorProviderProps) {
  const [state, dispatch] = useReducer(editorReducer, params, createInitialState);

  // Convenience methods
  const setMode = useCallback((mode: EditorMode, element?: Element | null) => {
    dispatch({ type: 'SET_MODE', mode, element });
  }, []);

  const closeEditor = useCallback(() => {
    dispatch({ type: 'SET_MODE', mode: null, element: null });
  }, []);

  const addInstruction = useCallback((instruction: Instruction) => {
    dispatch({ type: 'ADD_INSTRUCTION', instruction });
  }, []);

  const addHistoryAction = useCallback((action: HistoryAction) => {
    dispatch({ type: 'ADD_HISTORY_ACTION', action });
  }, []); // Remove state dependencies to prevent unnecessary recreations

  // Helper to apply a history action (for undo/redo)
  const applyHistoryAction = useCallback((action: HistoryAction, isUndo: boolean) => {
    const value = isUndo ? action.oldValue : action.newValue;
    const element = document.querySelector(action.selector);

    if (!element) {
      console.error('❌ [HMBL Undo] Could not find element for undo/redo. selector:', action.selector, 'type:', action.type, 'isUndo:', isUndo);
      return false;
    }
    console.log('[HMBL Undo] applyHistoryAction — type:', action.type, 'isUndo:', isUndo, 'selector:', action.selector);

    switch (action.type) {
      case 'text':
        element.textContent = value;
        break;
      case 'css':
      case 'style':
        if (typeof value === 'object') {
          Object.entries(value).forEach(([prop, val]) => {
            // If value is empty string, remove the inline style property
            // This properly restores elements that had no inline style originally
            if (val === '' || val === null || val === undefined) {
              (element as HTMLElement).style.removeProperty(prop);
            } else {
              (element as HTMLElement).style[prop as any] = val as string;
            }
          });
        }
        break;
      case 'image':
        if (element.tagName === 'IMG') {
          (element as HTMLImageElement).src = value;
        }
        break;
      case 'delete':
        // For delete, undo means restore, redo means delete again
        if (isUndo && action.html) {
          // Restore the deleted element
          const parent = element.parentElement;
          if (parent) {
            const temp = document.createElement('div');
            temp.innerHTML = action.html;
            const restoredEl = temp.firstElementChild;
            if (restoredEl) {
              parent.insertBefore(restoredEl, element.nextSibling);
            }
          }
        } else {
          element.remove();
        }
        break;
      case 'duplicate':
        if (isUndo && action.duplicatedElement) {
          // Remove the duplicated element
          action.duplicatedElement.remove();
        }
        break;
      case 'reorder': {
        // For reorder, undo means move in the opposite direction
        const direction = isUndo ? action.oldValue : action.newValue;
        if (direction === 'up') {
          const prev = element.previousElementSibling;
          if (prev) element.parentNode?.insertBefore(element, prev);
        } else if (direction === 'down') {
          const next = element.nextElementSibling;
          if (next) element.parentNode?.insertBefore(element, next.nextSibling);
        }
        break;
      }
      case 'visibility': {
        // Visibility is handled the same as style — apply old/new display value
        if (typeof value === 'object') {
          Object.entries(value).forEach(([prop, val]) => {
            if (val === '' || val === null || val === undefined) {
              (element as HTMLElement).style.removeProperty(prop);
            } else {
              (element as HTMLElement).style[prop as any] = val as string;
            }
          });
        }
        break;
      }
      default:
        console.warn('[EditorContext] Unknown history action type:', action.type);
        return false;
    }
    return true;
  }, []);

  const undo = useCallback(() => {
    console.log('[HMBL Undo] undo() called, historyIndex:', state.historyIndex, '/ historyLength:', state.history.length, 'onUndo?', !!state.params.onUndo);
    // If parent provided onUndo callback (iframe mode), use it instead
    if (state.params.onUndo) {
      state.params.onUndo();
      dispatch({ type: 'UNDO' }); // Still update local state for UI
      return;
    }

    if (state.historyIndex >= 0) {
      const action = state.history[state.historyIndex];
      console.log('[HMBL Undo] applying history action type:', action?.type, 'selector:', action?.selector);
      if (action && applyHistoryAction(action, true)) {
        console.log('[HMBL Undo] ✓ applyHistoryAction succeeded');
        // Also remove the corresponding instruction
        dispatch({ type: 'REMOVE_LAST_INSTRUCTION' });
      } else {
        console.error('❌ [HMBL Undo] applyHistoryAction failed — action:', action);
      }
      dispatch({ type: 'UNDO' });
    } else {
      console.warn('⚠️ [HMBL Undo] Cannot undo - historyIndex is', state.historyIndex);
    }
  }, [state.historyIndex, state.history, state.deploymentMode, state.params.onUndo, applyHistoryAction]);

  const redo = useCallback(() => {
    console.log('[HMBL Undo] redo() called, historyIndex:', state.historyIndex, '/ historyLength:', state.history.length, 'onRedo?', !!state.params.onRedo);
    // If parent provided onRedo callback (iframe mode), use it instead
    if (state.params.onRedo) {
      state.params.onRedo();
      dispatch({ type: 'REDO' }); // Still update local state for UI
      return;
    }

    if (state.historyIndex < state.history.length - 1) {
      const action = state.history[state.historyIndex + 1];
      console.log('[HMBL Undo] redo applying action type:', action?.type, 'selector:', action?.selector);
      if (action) {
        if (applyHistoryAction(action, false)) {
          // Re-add the instruction
          dispatch({
            type: 'ADD_INSTRUCTION',
            instruction: {
              selector: action.selector,
              op: action.type as any,
              value: action.newValue,
            },
          });
        } else {
          console.error('❌ [EditorContext] Redo failed to apply history action');
        }
      }
      dispatch({ type: 'REDO' });
    } else {
      console.warn('⚠️ [EditorContext] Cannot redo - at end of history');
    }
  }, [state.historyIndex, state.history, state.params.onRedo, applyHistoryAction]);

  const showToast = useCallback(
    (message: string, variant: Toast['variant'] = 'info', description?: string, duration = 4000) => {
      const toast: Toast = {
        id: `toast-${Date.now()}-${Math.random()}`,
        message,
        variant,
        description,
        duration,
      };
      dispatch({ type: 'ADD_TOAST', toast });
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', id });
  }, []);

  // Helper to apply instructions to DOM
  const applyInstructionsToDOM = useCallback((instructions: Instruction[]) => {
    instructions.forEach((instruction) => {
      const element = document.querySelector(instruction.selector);
      if (!element) return;

      switch (instruction.op) {
        case 'text': {
          const text = instructionTextValue(instruction.value);
          if (text != null) element.textContent = text;
          break;
        }
        case 'css':
          if (typeof instruction.value === 'object') {
            Object.entries(instruction.value).forEach(([prop, val]) => {
              (element as HTMLElement).style[prop as any] = val as string;
            });
          }
          break;
        case 'image':
          if (element.tagName === 'IMG') {
            (element as HTMLImageElement).src = instruction.value;
          }
          break;
        case 'delete':
          element.remove();
          break;
        case 'duplicate':
          if (instruction.value?.html) {
            const temp = document.createElement('div');
            temp.innerHTML = instruction.value.html;
            const cloned = temp.firstElementChild;
            if (cloned) {
              const pos = instruction.value.position || 'after';
              if (pos === 'after') {
                element.parentNode?.insertBefore(cloned, element.nextSibling);
              } else if (pos === 'before') {
                element.parentNode?.insertBefore(cloned, element);
              }
            }
          }
          break;
        case 'reorder': {
          const dir = instruction.value?.direction;
          if (dir === 'up') {
            const prev = element.previousElementSibling;
            if (prev) element.parentNode?.insertBefore(element, prev);
          } else if (dir === 'down') {
            const next = element.nextElementSibling;
            if (next) element.parentNode?.insertBefore(element, next.nextSibling);
          }
          break;
        }
        case 'visibility':
          if (typeof instruction.value === 'object') {
            Object.entries(instruction.value).forEach(([prop, val]) => {
              if (val === '' || val === null || val === undefined) {
                (element as HTMLElement).style.removeProperty(prop);
              } else {
                (element as HTMLElement).style[prop as any] = val as string;
              }
            });
          }
          break;
      }
    });
  }, []);

  const hydrateInstructions = useCallback((instructions: Instruction[]) => {
    applyInstructionsToDOM(instructions);
    dispatch({ type: 'SET_INSTRUCTIONS', instructions });
  }, [applyInstructionsToDOM]);

  // Reset DOM to original state
  const resetDOMToOriginal = useCallback(() => {
    if (state.originalBodyHTML && typeof document !== 'undefined') {
      // Find the main content area (not editor UI)
      const editorElements = document.querySelectorAll('.hmbl-editor-ui, .hmbl-hover-actions-wrapper, .hmbl-highlight, .hmbl-highlight-selected');
      
      // Store editor elements temporarily
      const editorUIHTML: { parent: ParentNode | null; html: string; ref: Element }[] = [];
      editorElements.forEach((el) => {
        if (el.classList.contains('hmbl-highlight')) {
          el.classList.remove('hmbl-highlight');
        }
        if (el.classList.contains('hmbl-highlight-selected')) {
          el.classList.remove('hmbl-highlight-selected');
        }
      });
      
      // For now, just remove highlight classes - full DOM reset would require page reload
      // In production, variant switching should reload the iframe with different instructions
    }
  }, [state.originalBodyHTML]);

  // Variant management
  const switchVariant = useCallback((variantId: VariantId) => {
    if (variantId === state.currentVariantId) {
      return;
    }
    
    // If parent provided callback (iframe mode), delegate to it
    if (state.params.onSwitchVariant) {
      state.params.onSwitchVariant(variantId);
      // Still update local state for UI
      dispatch({ type: 'SWITCH_VARIANT', variantId });
      return;
    }
    
    dispatch({ type: 'SET_LOADING_VARIANT', isLoading: true });
    
    // Save current variant's instructions first
    dispatch({
      type: 'UPDATE_VARIANT',
      variantId: state.currentVariantId,
      updates: { instructions: [...state.instructions] },
    });
    
    // Reset DOM to original state (remove applied changes)
    resetDOMToOriginal();
    
    // Switch to new variant (this loads the new variant's instructions into state)
    dispatch({ type: 'SWITCH_VARIANT', variantId });
    
    // Get the target variant's instructions and apply them
    const targetVariant = state.variants.get(variantId);
    if (targetVariant && targetVariant.instructions.length > 0) {
      // Apply after a small delay to ensure state update
      setTimeout(() => {
        applyInstructionsToDOM(targetVariant.instructions);
      }, 50);
    }
    
    dispatch({ type: 'SET_LOADING_VARIANT', isLoading: false });
  }, [state.currentVariantId, state.variants, state.instructions, state.deploymentMode, state.params.onSwitchVariant, resetDOMToOriginal, applyInstructionsToDOM]);

  // Create variant WITHOUT auto-switching (let caller handle switch)
  const createVariant = useCallback((id: VariantId, fromCurrent = false) => {
    // First save current variant
    dispatch({
      type: 'UPDATE_VARIANT',
      variantId: state.currentVariantId,
      updates: { instructions: [...state.instructions] },
    });
    
    // Create new variant (empty or copy of current)
    const newVariant: Variant = {
      id,
      name: `Variant ${id}`,
      instructions: fromCurrent ? [...state.instructions] : [],
      createdAt: new Date(),
      modifiedAt: new Date(),
      isControl: false,
    };
    dispatch({ type: 'CREATE_VARIANT', variant: newVariant });
    
    // NOTE: Caller should call switchVariant() after this if they want to switch
  }, [state.instructions, state.currentVariantId]);

  const getCurrentVariant = useCallback(() => {
    return state.variants.get(state.currentVariantId);
  }, [state.variants, state.currentVariantId]);

  const getVariantList = useCallback(() => {
    return Array.from(state.variants.values()).sort((a, b) => 
      a.id.localeCompare(b.id)
    );
  }, [state.variants]);

  // AI features
  const setAIPreview = useCallback((preview: AIPreview | null) => {
    dispatch({ type: 'SET_AI_PREVIEW', preview });
  }, []);

  const applyAIPreview = useCallback(() => {
    if (!state.aiPreview) return;

    const { element, variation, originalState } = state.aiPreview;
    const selector = element.id ? `#${element.id}` : element.tagName?.toLowerCase() || 'unknown';
    // Try to get a real selector from DOM
    let resolvedSelector = selector;
    try {
      if (typeof document !== 'undefined' && element instanceof Element) {
        // Use the element directly if it's in the DOM
        const el = element as HTMLElement;
        resolvedSelector = el.id ? `#${el.id}` : el.tagName.toLowerCase();
      }
    } catch { /* fallback to basic selector */ }

    const changes = variation.changes;

    // Apply text changes
    if (changes.text !== undefined) {
      const oldText = originalState.text ?? '';
      (element as HTMLElement).textContent = changes.text;
      addHistoryAction({
        type: 'text',
        selector: resolvedSelector,
        oldValue: oldText,
        newValue: changes.text,
      });
      addInstruction({
        selector: resolvedSelector,
        op: 'text',
        value: changes.text,
        aiGenerated: true,
        timestamp: new Date(),
      });
    }

    // Apply style changes
    if (changes.styles && Object.keys(changes.styles).length > 0) {
      const oldStyles = originalState.styles ?? {};
      Object.entries(changes.styles).forEach(([prop, val]) => {
        (element as HTMLElement).style[prop as any] = val;
      });
      addHistoryAction({
        type: 'style',
        selector: resolvedSelector,
        oldValue: oldStyles,
        newValue: changes.styles,
      });
      addInstruction({
        selector: resolvedSelector,
        op: 'css',
        value: changes.styles,
        aiGenerated: true,
        timestamp: new Date(),
      });
    }

    // Clear preview
    dispatch({ type: 'SET_AI_PREVIEW', preview: null });
    showToast('AI suggestion applied!', 'success');
  }, [state.aiPreview, showToast, addHistoryAction, addInstruction]);

  const cancelAIPreview = useCallback(() => {
    if (!state.aiPreview) return;
    
    // Restore original state
    const { element, originalState } = state.aiPreview;
    
    // Restore based on what was changed
    if (originalState.text !== undefined) {
      (element as HTMLElement).textContent = originalState.text;
    }
    if (originalState.styles) {
      Object.entries(originalState.styles).forEach(([prop, value]) => {
        (element as HTMLElement).style[prop as any] = value;
      });
    }
    
    dispatch({ type: 'SET_AI_PREVIEW', preview: null });
  }, [state.aiPreview]);

  // Draft mode
  const enableDraftMode = useCallback(() => {
    dispatch({ type: 'SET_DRAFT_MODE', enabled: true });
  }, []);

  const disableDraftMode = useCallback(() => {
    dispatch({ type: 'SET_DRAFT_MODE', enabled: false });
  }, []);

  const togglePreviewMode = useCallback(() => {
    dispatch({ type: 'TOGGLE_PREVIEW_MODE' });
  }, []);

  // Viewport control
  const setViewport = useCallback((viewport: ViewportSize) => {
    dispatch({ type: 'SET_VIEWPORT', viewport });
  }, []);

  // Save with better handling
  const saveChanges = useCallback(async () => {
    try {
      dispatch({ type: 'SET_SAVING', isSaving: true });
      
      const response = await fetch(
        'https://app.humblytics.com/api/v1/test/instructions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${state.params.authToken}`,
          },
          body: JSON.stringify({
            testId: state.params.testId,
            variantId: state.currentVariantId,
            instructions: state.instructions,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.message || 'Failed to save changes');
        error.status = response.status;
        throw error;
      }

      // Update variant with saved instructions
      dispatch({
        type: 'UPDATE_VARIANT',
        variantId: state.currentVariantId,
        updates: { instructions: [...state.instructions] },
      });
      
      dispatch({ type: 'CLEAR_INSTRUCTIONS' });
      showToast(
        `Saved ${state.instructions.length} change${state.instructions.length === 1 ? '' : 's'} to Variant ${state.currentVariantId}`,
        'success'
      );
    } catch (error) {
      const { message, description } = formatErrorForToast(error, 'save');
      showToast(message, 'error', description);
      console.error('[HmblEditor] Save error:', error);
      throw error;
    } finally {
      dispatch({ type: 'SET_SAVING', isSaving: false });
    }
  }, [state.params, state.currentVariantId, state.instructions, showToast]);

  const canUndo = state.historyIndex >= 0;
  const canRedo = state.historyIndex < state.history.length - 1;
  
  const value: EditorContextValue = {
    state,
    dispatch,
    setMode,
    closeEditor,
    addInstruction,
    addHistoryAction,
    undo,
    redo,
    canUndo,
    canRedo,
    showToast,
    removeToast,
    switchVariant,
    createVariant,
    getCurrentVariant,
    getVariantList,
    setAIPreview,
    applyAIPreview,
    cancelAIPreview,
    enableDraftMode,
    disableDraftMode,
    togglePreviewMode,
    saveChanges,
    setViewport,
    hydrateInstructions,
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

// Hook to use editor context
export function useEditor() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within EditorProvider');
  }
  return context;
}

